import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  BarcodeOptions,
  SettingsAccessControl,
  StoreConfigFlags,
} from 'types/settings/settingsApiTypes';
import {
  apiToBarcode,
  apiToDocument,
  apiToInvoiceCode,
  apiToPoints,
  apiToPrice,
  apiToRounding,
  apiToServiceCharge,
  apiToWeightBarcode,
  barcodeToApi,
  documentToApi,
  invoiceCodeToApi,
  pointsToApi,
  priceToApi,
  roundingToApi,
  serviceChargeToApi,
  weightBarcodeToApi,
} from 'utils/mappers/settingsMapper';
import {
  saveBarcode,
  saveDocument,
  saveInvoiceCode,
  savePoints,
  savePrice,
  saveRounding,
  saveServiceCharge,
  saveWeightBarcode,
} from 'services/settingsService';
import {
  SettingsFormValues,
  SettingsSectionKey,
} from 'pages/SettingsV2/settingsTypes';

export interface SettingsRestrictionNotice {
  titleKey: string;
  bodyKey: string;
  ctaKey?: string;
}

interface SettingsContextValue {
  flags: StoreConfigFlags;
  access: SettingsAccessControl;
  barcodeOptions: BarcodeOptions | null;
  savingSection: SettingsSectionKey | null;
  fieldErrors: Partial<Record<string, string>>;
  isDirty: (section: SettingsSectionKey) => boolean;
  isSectionAccessible: (section: SettingsSectionKey) => boolean;
  getSectionRestrictionNotice: (
    section: SettingsSectionKey
  ) => SettingsRestrictionNotice | null;
  saveSection: (section: SettingsSectionKey) => void;
  cancelSection: (section: SettingsSectionKey) => void;
  clearFieldError: (fieldPath: string) => void;
  validateField: (fieldPath: string) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export const useSettings = (): SettingsContextValue => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
};

interface SettingsProviderProps {
  flags: StoreConfigFlags;
  access: SettingsAccessControl;
  barcodeOptions: BarcodeOptions | null;
  initialData: SettingsFormValues;
  children: ReactNode;
}

type SectionHandler = {
  field: keyof SettingsFormValues;
  toApi: (values: SettingsFormValues) => unknown;
  service: (payload: never) => Promise<{
    message: string;
    error_code: number;
    data: unknown;
  }>;
  fromApi: (data: never) => unknown;
};

const SECTION_HANDLERS: Record<SettingsSectionKey, SectionHandler> = {
  productPrice: {
    field: 'productPrice',
    toApi: (v) => priceToApi(v.productPrice),
    service: savePrice as SectionHandler['service'],
    fromApi: apiToPrice as SectionHandler['fromApi'],
  },
  barcode: {
    field: 'barcode',
    toApi: (v) => barcodeToApi(v.barcode),
    service: saveBarcode as SectionHandler['service'],
    fromApi: apiToBarcode as SectionHandler['fromApi'],
  },
  weightBarcode: {
    field: 'weightBarcode',
    toApi: (v) => weightBarcodeToApi(v.weightBarcode),
    service: saveWeightBarcode as SectionHandler['service'],
    fromApi: apiToWeightBarcode as SectionHandler['fromApi'],
  },
  document: {
    field: 'document',
    toApi: (v) => documentToApi(v.document),
    service: saveDocument as SectionHandler['service'],
    fromApi: apiToDocument as SectionHandler['fromApi'],
  },
  rounding: {
    field: 'rounding',
    toApi: (v) => roundingToApi(v.rounding),
    service: saveRounding as SectionHandler['service'],
    fromApi: apiToRounding as SectionHandler['fromApi'],
  },
  loyaltyPoints: {
    field: 'loyaltyPoints',
    toApi: (v) => pointsToApi(v.loyaltyPoints),
    service: savePoints as SectionHandler['service'],
    fromApi: apiToPoints as SectionHandler['fromApi'],
  },
  serviceCharge: {
    field: 'serviceCharge',
    toApi: (v) => serviceChargeToApi(v.serviceCharge),
    service: saveServiceCharge as SectionHandler['service'],
    fromApi: apiToServiceCharge as SectionHandler['fromApi'],
  },
  invoiceCode: {
    field: 'invoiceCode',
    toApi: (v) => invoiceCodeToApi(v.invoiceCode),
    service: saveInvoiceCode as SectionHandler['service'],
    fromApi: apiToInvoiceCode as SectionHandler['fromApi'],
  },
};

const getSectionValidationErrors = (
  section: SettingsSectionKey,
  values: SettingsFormValues
): Partial<Record<string, string>> => {
  const hasUpToTwoDecimals = (value: number) =>
    Math.abs(value * 100 - Math.round(value * 100)) < 1e-9;

  switch (section) {
    case 'productPrice':
      return !values.productPrice.groupPriceNames[0]?.trim()
        ? {
            'productPrice.groupPriceNames[0]': 'group price (id 0) is required',
          }
        : {};
    case 'rounding':
      return values.rounding.condition > 0 &&
        (values.rounding.interval == null || values.rounding.interval <= 0)
        ? {
            'rounding.interval':
              'rounding interval is required when condition is set',
          }
        : {};
    case 'weightBarcode': {
      const wb = values.weightBarcode;
      const totalDigits =
        wb.prefixLength + wb.pluDigits + wb.weightDigits + wb.checkLength;
      const nextErrors: Partial<Record<string, string>> = {};

      if (!/^\d+$/.test(wb.prefix) || wb.prefix.length !== wb.prefixLength) {
        nextErrors['weightBarcode.prefix'] = 'invalid weight barcode format';
      }
      if (totalDigits !== 13) {
        nextErrors['weightBarcode.weightDigits'] =
          'invalid weight barcode format';
      }

      return nextErrors;
    }
    case 'loyaltyPoints':
      return values.loyaltyPoints.isActive &&
        values.loyaltyPoints.expiryYear <= 0
        ? {
            'loyaltyPoints.expiryYear':
              'expiry year is required when points are activated',
          }
        : {};
    case 'invoiceCode': {
      const ic = values.invoiceCode;
      if (
        ic.type === 2 &&
        (!ic.format || ic.num < 2 || ![1, 2].includes(ic.typeYear))
      ) {
        return {
          'invoiceCode.typeYear': 'invalid sale code format',
          'invoiceCode.format': 'invalid sale code format',
          'invoiceCode.num': 'invalid sale code format',
        };
      }
      return {};
    }
    case 'serviceCharge': {
      if (!values.serviceCharge.isActive) return {};

      const nextErrors: Partial<Record<string, string>> = {};
      const { percent, vat } = values.serviceCharge;

      if (
        !Number.isFinite(percent) ||
        percent < 0 ||
        percent > 100 ||
        !hasUpToTwoDecimals(percent)
      ) {
        nextErrors['serviceCharge.percent'] =
          'Enter a value between 0 and 100 (up to 2 decimals)';
      }

      if (
        !Number.isFinite(vat) ||
        vat < 0 ||
        vat > 100 ||
        !hasUpToTwoDecimals(vat)
      ) {
        nextErrors['serviceCharge.vat'] =
          'Enter a value between 0 and 100 (up to 2 decimals)';
      }

      return nextErrors;
    }
    default:
      return {};
  }
};

export const SettingsProvider = ({
  flags,
  access,
  barcodeOptions,
  initialData,
  children,
}: SettingsProviderProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<SettingsFormValues>();

  const [baseline, setBaseline] = useState<SettingsFormValues>(initialData);
  const [savingSection, setSavingSection] = useState<SettingsSectionKey | null>(
    null
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<string, string>>
  >({});

  useEffect(() => {
    setBaseline(initialData);
  }, [initialData]);

  const isDirty = useCallback(
    (section: SettingsSectionKey) =>
      JSON.stringify(values[SECTION_HANDLERS[section].field]) !==
      JSON.stringify(baseline[SECTION_HANDLERS[section].field]),
    [baseline, values]
  );

  const clearFieldError = useCallback((fieldPath: string) => {
    setFieldErrors((prev) => ({ ...prev, [fieldPath]: undefined }));
  }, []);

  const isSectionAccessible = useCallback(
    (section: SettingsSectionKey) => {
      switch (section) {
        case 'rounding':
          return access.rounding_bill;
        case 'loyaltyPoints':
          return access.points;
        case 'serviceCharge':
          return access.service_charge;
        default:
          return true;
      }
    },
    [access]
  );

  const getSectionRestrictionNotice = useCallback(
    (section: SettingsSectionKey): SettingsRestrictionNotice | null => {
      if (isSectionAccessible(section)) {
        return null;
      }

      return {
        titleKey: 'This section isn\'t available right now',
        bodyKey:
          'Your account or current plan doesn\'t support access to this section. Please contact your administrator or POS2U Support for help.',
        ctaKey: 'Contact Support',
      };
    },
    [isSectionAccessible]
  );

  const validateField = useCallback(
    (fieldPath: string) => {
      const nextErrors = Object.entries(
        getSectionValidationErrors(
          fieldPath.split('.')[0] as SettingsSectionKey,
          values
        )
      ).filter(([key]) => key === fieldPath);

      setFieldErrors((prev) => ({
        ...prev,
        [fieldPath]: nextErrors[0]?.[1],
      }));
    },
    [values]
  );

  const cancelSection = useCallback(
    (section: SettingsSectionKey) => {
      const handler = SECTION_HANDLERS[section];
      setFieldValue(handler.field, baseline[handler.field]);
      setFieldErrors((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${handler.field}.`)) {
            delete next[key];
          }
        });
        return next;
      });
    },
    [baseline, setFieldValue]
  );

  const saveSection = useCallback(
    async (section: SettingsSectionKey) => {
      const validationErrors = getSectionValidationErrors(section, values);
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...validationErrors }));
        return;
      }

      const handler = SECTION_HANDLERS[section];
      setSavingSection(section);
      try {
        const res = await handler.service(handler.toApi(values) as never);
        if (res.error_code === 0) {
          const nextSectionData = handler.fromApi(res.data as never);
          setBaseline((prev) => ({
            ...prev,
            [handler.field]: nextSectionData,
          }));
          setFieldValue(handler.field, nextSectionData);
          toast.success(t('Saved successfully'));
        } else {
          toast.error(t(res.message || 'Save failed'));
        }
      } catch (err: any) {
        toast.error(t(err?.message || 'Save failed'));
      } finally {
        setSavingSection(null);
      }
    },
    [setFieldValue, t, values]
  );

  const value = useMemo(
    () => ({
      flags,
      access,
      barcodeOptions,
      savingSection,
      fieldErrors,
      isDirty,
      isSectionAccessible,
      getSectionRestrictionNotice,
      saveSection,
      cancelSection,
      clearFieldError,
      validateField,
    }),
    [
      barcodeOptions,
      cancelSection,
      clearFieldError,
      access,
      fieldErrors,
      flags,
      isDirty,
      isSectionAccessible,
      getSectionRestrictionNotice,
      saveSection,
      savingSection,
      validateField,
    ]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
